'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Skeleton } from '@common/components';
import { SvgStarLine } from '@common/icon';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { AuthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { useJejuDonationValidation } from '@/app/donation/_hooks/useJejuDonationValidation';
import { DONATION_ROUTES } from '@/constants';
import { CertificateCard } from './CertificateCard';

// 연속 기부 년차 계산: 가장 최근 기부년도부터 연속된 횟수만 계산
// 예: 25년 기부 → 1년차, 25+26년 연속 → 2년차, 25년+27년(26년 X) → 1년차
const calculateConsecutiveDonationYears = (donationYears: number[]) => {
  if (donationYears.length === 0) return 0;
  const uniqueYears = [...new Set(donationYears)].sort((a, b) => b - a);
  const startYear = uniqueYears[0];
  const breakIndex = uniqueYears.findIndex((year, index) => year !== startYear - index);
  return breakIndex === -1 ? uniqueYears.length : breakIndex;
};
export const JejuCertificateContent = t0 => {
  const $ = _c(20);
  const {
    onClose
  } = t0;
  let t1;
  if ($[0] !== onClose) {
    t1 = {
      onFailed: () => {
        onClose();
      }
    };
    $[0] = onClose;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isDonatedToJeju
  } = useJejuDonationValidation(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = AuthQueryOptions["get:/wello/v2/auth/home-donation/certificate"]();
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isDonatedToJeju) {
    t3 = {
      ...t2,
      select: _temp,
      enabled: isDonatedToJeju
    };
    $[3] = isDonatedToJeju;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: certificate
  } = useQuery(t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = AuthQueryOptions["get:/wello/v2/auth/home-donation/history"]({
      query: {
        page: 1,
        size: 100
      }
    });
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== isDonatedToJeju) {
    t5 = {
      ...t4,
      select: _temp2,
      enabled: isDonatedToJeju
    };
    $[6] = isDonatedToJeju;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    data: donationHistory
  } = useQuery(t5);
  let t6;
  bb0: {
    if (!donationHistory) {
      t6 = null;
      break bb0;
    }
    let t7;
    if ($[8] !== donationHistory) {
      const completedDonationYears = donationHistory.filter(_temp3).map(_temp4);
      t7 = calculateConsecutiveDonationYears(completedDonationYears);
      $[8] = donationHistory;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    const consecutiveYears = t7;
    t6 = consecutiveYears > 0 ? consecutiveYears : null;
  }
  const donationYear = t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("flex flex-col items-center pt-32", "mobile-tablet:pt-0");
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== certificate || $[12] !== donationYear) {
    t8 = certificate ? <><CertificateCard year={donationYear ?? null} name={certificate.name} /><ul className="flex w-280 flex-col gap-8 px-12 py-32 [&>li>p]:font-600 [&>li>p]:text-Gray-800 [&>li>p]:typo-body3 [&>li>span]:font-400 [&>li>span]:text-Gray-600 [&>li>span]:typo-body3 [&>li]:flex [&>li]:items-center [&>li]:justify-between"><li><p>발급일</p><span>{dayjs(certificate.issuedDate).format("YYYY.MM.DD")}</span></li><li><p>기간</p><span>{dayjs(certificate.certificateIssueEndDate).format("YYYY.MM.DD")}</span></li><li /></ul></> : <div><Skeleton className="h-[406px] w-280 rounded-large" /><div className="flex h-128 flex-col justify-center gap-8"><Skeleton typo="body3" className="w-280" /><Skeleton typo="body3" className="w-280" /></div></div>;
    $[11] = certificate;
    $[12] = donationYear;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <SvgStarLine className="size-16 text-Gray-600" originalFill={true} />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== onClose) {
    t10 = <Button theme="primary-line" dimensions="H48" className="mt-auto w-280 gap-6" asChild={true}><CustomLink href={DONATION_ROUTES.MY_JEJU_BENEFIT_PUBLIC.pathname} aria-label={"\uC81C\uC8FC \uACF5\uC601\uAD00\uAD11\uC9C0 \uD61C\uD0DD \uC548\uB0B4 \uD398\uC774\uC9C0\uB85C \uC774\uB3D9"} onClick={onClose}>{t9}혜택보기</CustomLink></Button>;
    $[15] = onClose;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t8) {
    t11 = <article className={t7}>{t8}{t10}</article>;
    $[17] = t10;
    $[18] = t8;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  return t11;
};
function _temp(data) {
  return data.context;
}
function _temp2(data_0) {
  return data_0.context?.contents;
}
function _temp3(donation) {
  return donation.donationStatus === "COMPLETED";
}
function _temp4(donation_0) {
  return Number(donation_0.paidAt?.split(".")[0]);
}